const isDev = import.meta.env.DEV;

const API_BASE = isDev
  ? '/api' // usa proxy en desarrollo
  : `${import.meta.env.VITE_BACKEND_HOST}${import.meta.env.VITE_API_BASE || ''}`;
const isQtWebEngine = navigator.userAgent.includes('QtWebEngine');
const APP_SECRET = import.meta.env.VITE_APP_SECRET || '';

// Tiempo de margen para refrescar el token antes de que expire (en segundos)
const TOKEN_REFRESH_THRESHOLD = 300; // 5 minutos antes de expirar

function buildUrl(path) {
  // Limpia barras duplicadas
  const base = API_BASE.replace(/\/+$/, '');
  const cleanPath = path.replace(/^\/+/, '');
  return `${base}/${cleanPath}`.replace(/([^:]\/)\/+/g, '$1');
}

// Decodificar JWT sin verificar firma (para obtener datos del usuario)
function decodeJWT(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('[API] Error al decodificar JWT:', e);
    return null;
  }
}

// Verificar si el token está por expirar y refrescarlo proactivamente
function isTokenExpiringSoon(decoded) {
  if (!decoded || !decoded.exp) return false;
  const expirationTime = decoded.exp * 1000; // Convertir a milisegundos
  const currentTime = Date.now();
  const timeUntilExpiration = expirationTime - currentTime;
  return timeUntilExpiration < (TOKEN_REFRESH_THRESHOLD * 1000);
}

// Función proactiva para refrescar el token antes de que expire
async function ensureValidToken() {
  const token = localStorage.getItem('access');
  if (!token) return null;

  const decoded = decodeJWT(token);
  if (!decoded) return null;

  // Si el token está por expirar, refrescarlo
  if (isTokenExpiringSoon(decoded)) {
    console.log('[API] Token por expirar, refrescando proactivamente...');
    try {
      const newToken = await refreshAccessToken();
      return newToken;
    } catch (error) {
      console.error('[API] Error al refrescar token proactivamente:', error);
      return null;
    }
  }

  return token;
}

// Obtener usuario desde el token actual
function getUserFromToken() {
  const token = localStorage.getItem('access');
  if (!token) return null;

  const decoded = decodeJWT(token);
  if (!decoded) return null;

  // Convertir los datos del token al formato de usuario
  return {
    id: decoded.sub,
    usuario: decoded.username,
    nombre: decoded.nombre,
    rol: decoded.rol,
    id_sede: decoded.id_sede,
    id_sedes: decoded.id_sedes,
    id_asesor: decoded.id_asesor,
    es_socio: decoded.es_socio,
    es_lider: decoded.es_lider,
    cargo_id: decoded.cargo_id,
    codigo_vendedor: decoded.codigo_vendedor,
    cargo: decoded.cargo,
  };
}

// Actualizar usuario en localStorage desde el token
function syncUserFromToken() {
  const user = getUserFromToken();
  if (user) {
    localStorage.setItem('user', JSON.stringify(user));
  }
  return user;
}



// Timeout helper: aborta si el servidor no responde en N ms
function fetchWithTimeout(url, options = {}, timeout = 10000) {
  return Promise.race([
    fetch(url, options),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Timeout: El servidor no respondió')), timeout)
    ),
  ]);
}

// Variable para controlar si hay un refresh en progreso
let refreshPromise = null;

// Error personalizado para sesión expirada
class SessionExpiredError extends Error {
  constructor(message = 'Tu sesión ha expirado. Por favor inicia sesión nuevamente.') {
    super(message);
    this.name = 'SessionExpiredError';
    this.isSessionExpired = true;
  }
}

// Función para manejar el logout cuando la sesión expira
function handleSessionExpired() {
  localStorage.removeItem('access');
  localStorage.removeItem('refresh');
  localStorage.removeItem('user'); // Si guardas el usuario en localStorage
  // Recargar la página para volver al login
  window.location.href = '/app/login';
}

// Función para refrescar el token
async function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refresh');

  if (!refreshToken) {
    handleSessionExpired();
    throw new SessionExpiredError('No hay sesión activa. Por favor inicia sesión.');
  }

  try {
    const response = await fetch(buildUrl('/refresh/'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: refreshToken }),
      credentials: isQtWebEngine ? 'include' : 'omit',
    });

    // Manejar específicamente errores de autenticación
    if (response.status === 401) {
      handleSessionExpired();
      throw new SessionExpiredError('Tu sesión ha expirado. Por favor inicia sesión nuevamente.');
    }

    if (!response.ok) {
      handleSessionExpired();
      const text = await response.text().catch(() => '');
      let msg = 'Error al refrescar la sesión';
      try {
        const json = JSON.parse(text || '{}');
        msg = json.detail || json.message || msg;
      } catch {}
      throw new SessionExpiredError(msg);
    }

    const data = await response.json();
    if (!data.access) {
      handleSessionExpired();
      throw new SessionExpiredError('No se pudo obtener un nuevo token de acceso.');
    }

    localStorage.setItem('access', data.access);
    // Sincronizar usuario con el nuevo token
    syncUserFromToken();
    return data.access;
  } catch (error) {
    // Si ya es un SessionExpiredError, relanzarlo tal cual
    if (error.isSessionExpired) {
      throw error;
    }

    // Para otros errores (red, timeout, etc), limpiar tokens y crear SessionExpiredError
    handleSessionExpired();
    throw new SessionExpiredError('Error de conexión al refrescar la sesión. Por favor inicia sesión nuevamente.');
  }
}


// ---------------------------------------------
//  🚀 Función principal de request
// ---------------------------------------------

async function request(path, { method = 'GET', data, headers, timeout = 10000, skipAuth = false } = {}) {
  const url = buildUrl(path);

  // Función interna para hacer la request
  const makeRequest = async (token) => {
    const baseHeaders = {
      'Content-Type': 'application/json',
      ...(headers || {}),
    };

    if (token) {
      baseHeaders['Authorization'] = `Bearer ${token}`;
    }

    // En producción, agregamos el header secreto
    if (!isDev && APP_SECRET) {
      baseHeaders['X-Distritec-App'] = APP_SECRET;
    }

    if (isDev) console.debug('[API] →', method, url, data ? { ...data, clave: '***' } : '');

    const res = await fetchWithTimeout(
      url,
      {
        method,
        headers: baseHeaders,
        body: data ? JSON.stringify(data) : undefined,
        credentials: 'include',
        keepalive: false,
        cache: 'no-store',
        mode: 'cors',
      },
      timeout
    );

    const text = await res.text().catch(() => '');
    if (isDev) console.debug('[API] ←', res.status, text.slice(0, 150));

    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const json = JSON.parse(text || '{}');
        msg = json.detail || json.message || JSON.stringify(json);
      } catch {}
      const err = new Error(msg);
      err.status = res.status;
      err.response = text;
      throw err;
    }

    const ct = res.headers.get('content-type') || '';
    return ct.includes('application/json') ? JSON.parse(text || '{}') : text;
  };

  try {
    // 🔓 Si skipAuth es true, hacer la request sin token (para endpoints públicos como login)
    if (skipAuth) {
      if (isDev) console.debug('[API] Request pública (sin token)');
      return await makeRequest(null);
    }

    // 🔥 Verificar y refrescar el token proactivamente antes de la petición
    let token = await ensureValidToken();

    if (!token) {
      throw new SessionExpiredError('No hay token de acceso disponible');
    }

    try {
      // Primer intento con el token (posiblemente recién refrescado)
      return await makeRequest(token);
    } catch (err) {
      // Si es 401 y tenemos un refresh token, intentar refrescar
      if (err.status === 401 && localStorage.getItem('refresh')) {
        if (isDev) console.debug('[API] Token expirado, refrescando...');

        // Reusar el promise si ya hay un refresh en progreso
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken();
        }

        try {
          const newToken = await refreshPromise;

          // Si refreshAccessToken no lanzó error, significa que tenemos un nuevo token
          // NOTA: Si el refresh falló, handleSessionExpired() ya habrá redirigido al login
          token = newToken;

          // Reintentar la request con el nuevo token
          return await makeRequest(token);
        } catch (refreshErr) {
          // Si el error es SessionExpiredError, no reintentar
          if (refreshErr.isSessionExpired) {
            throw refreshErr;
          }
          // Para otros errores, reintentar de todas formas
          if (token) {
            return await makeRequest(token);
          }
          throw refreshErr;
        } finally {
          refreshPromise = null; // Limpiar el promise
        }
      }

      // Si no es 401 o no hay refresh token, lanzar el error
      throw err;
    }
  } catch (err) {
    // Si es SessionExpiredError, relanzarlo tal cual (ya se habrá redirigido al login)
    if (err.isSessionExpired) {
      throw err;
    }

    if (err.status) throw err;

    const e = new Error(
      err.message?.includes('Timeout')
        ? 'El servidor no responde. Verifica que el backend esté corriendo.'
        : `Error de conexión: ${err.message}`
    );
    e.isNetworkError = true;
    throw e;
  }
}


// ---------------------------------------------
//  🌐 API pública
// ---------------------------------------------

export const api = {
  post: (p, d, o) => request(p, { method: 'POST', data: d, ...(o || {}) }),
  get: (p, o) => request(p, { method: 'GET', ...(o || {}) }),
  put: (p, d, o) => request(p, { method: 'PUT', data: d, ...(o || {}) }),
  patch: (p, d, o) => request(p, { method: 'PATCH', data: d, ...(o || {}) }),
  delete: (p, o) => request(p, { method: 'DELETE', ...(o || {}) }),

  // 🔐 Login especializado (SIN token - endpoint público)
  login: async (creds) => {
    try {
      // Hacer request directa sin token para login
      const response = await request('/login/', {
        method: 'POST',
        data: creds,
        timeout: 15000,
        skipAuth: true, // 🔑 Indica que esta request no requiere token
      });

      // Guardar tokens en localStorage
      if (response.access) {
        localStorage.setItem('access', response.access);
      }
      if (response.refresh) {
        localStorage.setItem('refresh', response.refresh);
      }

      return response;
    } catch (err) {
      if (err.status === 401) throw new Error('Usuario o contraseña incorrectos');
      if (err.status === 403) throw new Error(err.message || 'No autorizado');
      if (err.isNetworkError) throw new Error('No se puede conectar con el servidor.');
      throw err;
    }
  },

  // 🔄 Logout
  logout: () => {
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    localStorage.removeItem('user');
  },

  // 👤 Obtener usuario actual (desde el token)
  getUser: () => getUserFromToken(),

  // 🔄 Sincronizar usuario desde el token al localStorage
  syncUser: () => syncUserFromToken(),

  // ✅ Verificar y refrescar token si está por expirar
  ensureValidToken: () => ensureValidToken(),
};

// Exportar el error personalizado y funciones útiles para que otros componentes lo puedan usar
export { SessionExpiredError, getUserFromToken, syncUserFromToken, ensureValidToken };